import { ChartDataPoint } from '../types';
import { COLORS, MODEL_COLORS } from '../config';

/**
 * Convert hex color to rgba with specified opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Extract all series keys from chart data across all time points
 */
export function getAllSeriesKeys(chartData: ChartDataPoint[]): string[] {
  const allKeysSet = new Set<string>();
  chartData.forEach((point) => {
    Object.keys(point).forEach((key) => {
      if (key !== 'time' && key !== 'Mean' && key !== 'Mean + Std' && key !== 'Mean - Std') {
        allKeysSet.add(key);
      }
    });
  });
  return Array.from(allKeysSet);
}

/**
 * Get fixed color for a model series name
 * Series names come in format: "ModelName | YYYY-MM-DD HH:mm"
 */
function getModelColor(seriesName: string, fallbackIndex: number): string {
  // Extract the model name part (before the " | " separator)
  const modelName = seriesName.split(' | ')[0].trim();

  // First try exact match with the extracted model name
  if (MODEL_COLORS[modelName]) {
    return MODEL_COLORS[modelName];
  }

  // Try to find partial matches for common model patterns
  const normalizedName = modelName.toLowerCase();

  // Check for EPT models with more specific patterns
  if (normalizedName.includes('ept-1.5') || normalizedName === 'ept-1.5') {
    return MODEL_COLORS['EPT-1.5'];
  }
  if (normalizedName.includes('ept-2') && !normalizedName.includes('1.5')) {
    return MODEL_COLORS['EPT-2'];
  }
  if (normalizedName.includes('ept') && normalizedName.includes('early')) {
    if (normalizedName.includes('1.5')) {
      return MODEL_COLORS['EPT-1.5 Early'];
    }
    if (normalizedName.includes('2')) {
      return MODEL_COLORS['EPT-2 Early'];
    }
  }

  // Check for ECMWF models
  if (normalizedName.includes('ecmwf') || normalizedName.includes('ec ')) {
    if (normalizedName.includes('ensemble')) {
      return MODEL_COLORS['ECMWF Ensemble Mean'];
    }
    if (normalizedName.includes('aifs') || normalizedName.includes('post-processed')) {
      return MODEL_COLORS['EC AIFS (Post-processed)'];
    }
    return MODEL_COLORS['ECMWF'];
  }

  // Check for specific AIFS without ECMWF prefix
  if (normalizedName.includes('aifs')) {
    return MODEL_COLORS['EC AIFS'];
  }

  // Check for GFS models
  if (normalizedName.includes('gfs')) {
    if (normalizedName.includes('ensemble')) {
      return MODEL_COLORS['GFS Ensemble Mean'];
    }
    if (normalizedName.includes('graphcast')) {
      return MODEL_COLORS['GFS Graphcast'];
    }
    return MODEL_COLORS['GFS'];
  }

  // Check for ICON models
  if (normalizedName.includes('icon')) {
    if (normalizedName.includes('global')) {
      return MODEL_COLORS['ICON Global'];
    }
    return MODEL_COLORS['ICON-EU'];
  }

  // Check for other specific models
  if (normalizedName.includes('aurora')) {
    return MODEL_COLORS['Microsoft Aurora'];
  }
  if (normalizedName.includes('ukmo')) {
    return MODEL_COLORS['UKMO'];
  }
  if (normalizedName.includes('météo') || normalizedName.includes('meteo')) {
    return MODEL_COLORS['Météo-France'];
  }

  // Fallback to original color cycling for unknown models
  return COLORS[fallbackIndex % COLORS.length];
}

/**
 * Create model run series for ECharts
 */
export function createModelRunSeries(
  chartData: ChartDataPoint[],
  dataKeys: string[],
  useAreaStyle: boolean = false,
): any[] {
  return dataKeys.map((seriesName, index) => {
    const seriesData = chartData.map((point) => [point.time, point[seriesName]]);
    const seriesColor = getModelColor(seriesName, index);

    // Filter out null/undefined values
    const validSeriesData = seriesData.filter(
      ([time, value]) => time != null && value != null && !isNaN(value as number),
    );

    const series: any = {
      name: seriesName,
      type: 'line',
      data: validSeriesData,
      lineStyle: {
        color: seriesColor,
        width: 2,
      },
      itemStyle: {
        color: seriesColor, // This is what the legend uses for color
      },
      symbol: 'none',
      connectNulls: false,
      smooth: 0.1,
      z: 2, // Ensure model lines appear above the statistical area
    };

    // Only add area style when requested (for delta mode)
    if (useAreaStyle) {
      series.areaStyle = {
        color: hexToRgba(seriesColor, 0.15), // Add semi-transparent area fill (15% opacity)
      };
    }

    return series;
  });
}

/**
 * Create statistical series for ECharts
 */
export function createStatisticalSeries(chartData: ChartDataPoint[], isDark: boolean): any[] {
  // Filter to only points that have valid Mean values
  const validPoints = chartData.filter(
    (point) =>
      point['Mean'] !== undefined &&
      point['Mean'] !== null &&
      Number.isFinite(point['Mean'] as number),
  );

  if (validPoints.length === 0) {
    return [];
  }

  // Use more visible colors instead of black/white which might not show up
  const meanColor = isDark ? '#E5E7EB' : '#374151'; // Light gray in dark mode, dark gray in light mode
  const stdColor = isDark ? '#9CA3AF' : '#6B7280'; // Medium gray

  return [
    // Standard deviation area band using polygon
    {
      name: 'Standard Deviation Band',
      type: 'custom',
      renderItem: (params: any, api: any) => {
        // Only use points with valid Mean values for the polygon
        const points = validPoints.map((point) => {
          const upperPoint = api.coord([point.time, point['Mean + Std']]);
          const lowerPoint = api.coord([point.time, point['Mean - Std']]);
          return { upper: upperPoint, lower: lowerPoint };
        });

        const polygonPoints: number[][] = [];

        // Add upper boundary points (left to right)
        points.forEach((p) => polygonPoints.push(p.upper));

        // Add lower boundary points (right to left)
        points
          .slice()
          .reverse()
          .forEach((p) => polygonPoints.push(p.lower));

        return {
          type: 'polygon',
          shape: {
            points: polygonPoints,
          },
          style: {
            fill: isDark ? 'rgba(156, 163, 175, 0.15)' : 'rgba(102, 102, 102, 0.15)',
            stroke: 'transparent',
          },
        };
      },
      data: [null], // Single data point to trigger render
      itemStyle: {
        color: stdColor, // Legend color
      },
      z: 1,
    },
    // Mean line on top - only include valid data points
    {
      name: 'Mean',
      type: 'line',
      data: validPoints.map((point) => [point.time, point['Mean']]),
      lineStyle: {
        color: meanColor,
        width: 3,
        type: 'dashed',
      },
      itemStyle: {
        color: meanColor, // Ensure legend color matches line color
      },
      symbol: 'none',
      connectNulls: true, // Connect across any remaining gaps
      z: 10, // Ensure mean line appears on top
    },
  ];
}
